/**
 * Model-readiness report page (spec §2 item 1): capability cards plus the
 * generation gate banner. Generation stays disabled until the gates clear —
 * never confident-wrong output.
 */
import { useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { ReadinessCards } from "../components/ReadinessCards";
import { useIstariStore } from "../state/store";

export function ReadinessPage() {
  const params = useParams({ strict: false }) as { modelId?: string };
  const modelId = params.modelId ?? "";

  const report = useIstariStore((s) => s.readiness[modelId]);
  const status = useIstariStore((s) => s.readinessStatus[modelId] ?? "idle");
  const loadReadiness = useIstariStore((s) => s.loadReadiness);
  const model = useIstariStore((s) => s.models.find((m) => m.modelId === modelId) ?? null);

  useEffect(() => {
    if (modelId && status === "idle") void loadReadiness(modelId);
  }, [modelId, status, loadReadiness]);

  return (
    <main
      style={{
        fontFamily: "sans-serif",
        padding: "2rem",
        maxWidth: 860,
        margin: "0 auto",
        background: "#1a1a2e",
        color: "#d8d8e8",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <nav style={{ marginBottom: "1rem", display: "flex", gap: "1rem" }}>
        {model ? (
          <a href={`/projects/${model.projectId}`} style={{ color: "#8e8eb8" }}>
            ← {model.fileName}
          </a>
        ) : (
          <a href="/" style={{ color: "#8e8eb8" }}>
            ← Projects
          </a>
        )}
        <a href={`/models/${modelId}/normalize`} style={{ color: "#4a90d9" }}>
          Normalization wizard
        </a>
        <a
          data-testid="readiness-mep-link"
          href={`/models/${modelId}/mep`}
          style={{ color: "#4a90d9" }}
        >
          MEP generation →
        </a>
      </nav>
      <h1 style={{ marginTop: 0, fontSize: "1.4rem" }}>
        Model readiness
        {model && <span style={{ color: "#8e8eb8", fontWeight: 400 }}> — {model.fileName}</span>}
      </h1>
      <p style={{ color: "#8e8eb8", fontSize: "0.85rem" }}>
        Generation features stay disabled until their required model data is present (rule-checked
        design assistance needs trustworthy inputs).
      </p>

      {status === "loading" && <p>Loading readiness report…</p>}
      {status === "error" && (
        <div data-testid="readiness-error" style={{ color: "#e06c75" }}>
          <p>Could not load the readiness report.</p>
          <button
            type="button"
            onClick={() => void loadReadiness(modelId)}
            style={{
              padding: "0.4rem 0.8rem",
              borderRadius: 6,
              border: "1px solid #4a90d9",
              background: "transparent",
              color: "#4a90d9",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}
      {report && (
        <ReadinessCards
          report={report}
          normalizeHref={`/models/${modelId}/normalize`}
          projectHref={model ? `/projects/${model.projectId}` : undefined}
        />
      )}
    </main>
  );
}
